import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LogoutIcon from "@mui/icons-material/LogoutOutlined";
import PersonAddIcon from "@mui/icons-material/PersonAddOutlined";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import { useAuth } from "../contexts/AuthContext";
import { useLocale } from "../contexts/LocaleContext";
import LanguageSelector from "./LanguageSelector";
import { createTeam, getInviteLink, getUserProfile } from "../utils/firebase";

function getInitials(email) {
  if (!email) return "?";
  const local = email.split("@")[0];
  if (local.length >= 2) return (local[0] + local[1]).toUpperCase();
  return local[0].toUpperCase();
}

function avatarColor(email) {
  if (!email) return "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)";
  let hash = 0;
  for (let i = 0; i < email.length; i++) hash = email.charCodeAt(i) + ((hash << 5) - hash);
  const hues = [262, 221, 199, 173, 142];
  const h = hues[Math.abs(hash) % hues.length];
  return `linear-gradient(135deg, hsl(${h}, 70%, 55%) 0%, hsl(${h}, 65%, 45%) 100%)`;
}

export default function TopHeader({ handleDrawerToggle }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const { localizePath } = useLocale();

  const [anchorEl, setAnchorEl] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [canManageMeetings, setCanManageMeetings] = useState(false);
  const [teamNameDialogOpen, setTeamNameDialogOpen] = useState(false);
  const [teamName, setTeamName] = useState("");

  useEffect(() => {
    if (!user) return;
    getUserProfile()
      .then((r) => {
        const { teamId: tid, teamRole: role } = r.data || {};
        setCanManageMeetings(!tid || role === "admin");
      })
      .catch(() => setCanManageMeetings(false));
  }, [user]);

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const copyInviteUrl = (inviteToken) => {
    const base = localizePath("/invite").replace("/invite", "");
    const url = `${window.location.origin}${base}/invite/${inviteToken}`;
    navigator.clipboard.writeText(url)
      .then(() => {
        setInviteCopied(true);
        setTimeout(() => setInviteCopied(false), 2000);
      })
      .catch(() => {
        setInviteError(url);
        setTimeout(() => setInviteError(null), 8000);
      });
  };

  const handleInvite = async () => {
    if (!user || !canManageMeetings) return;
    setInviteLoading(true);
    setInviteCopied(false);
    setInviteError(null);
    try {
      const result = await getInviteLink();
      const inviteToken = result.data.inviteToken;
      copyInviteUrl(inviteToken);
    } catch (err) {
      if (err.code === "functions/permission-denied" || err.code === "functions/failed-precondition") {
        setTeamName("");
        setTeamNameDialogOpen(true);
      } else {
        setInviteError(err.message || t("header.failedInviteLink"));
        setTimeout(() => setInviteError(null), 5000);
      }
    } finally {
      setInviteLoading(false);
      handleMenuClose();
    }
  };

  const handleCreateTeamWithName = async () => {
    const name = teamName.trim();
    if (!name) return;
    setInviteLoading(true);
    setInviteError(null);
    try {
      const result = await createTeam({ teamName: name });
      setTeamNameDialogOpen(false);
      setTeamName("");
      setCanManageMeetings(true);
      copyInviteUrl(result.data.inviteToken);
    } catch (err) {
      setInviteError(err.message || t("header.failedCreateTeam"));
      setTimeout(() => setInviteError(null), 5000);
    } finally {
      setInviteLoading(false);
    }
  };

  const handleSignOut = async () => {
    await logout();
    handleMenuClose();
    if (location.pathname.includes("/call/") || location.pathname === "/" || location.pathname === "/es") {
      navigate(localizePath("/login"));
    }
  };

  return (
    <Box
      component="header"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1000,
        bgcolor: "#ffffff",
        borderBottom: "none",
        height: 56,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: { xs: 2, md: 3 },
      }}
    >
      {/* Left side: Hamburger and Logo (mobile only) */}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{ display: { xs: "flex", md: "none" }, mr: 1.5, color: "#111827" }}
        >
          <MenuIcon />
        </IconButton>
        <Typography
          variant="h6"
          sx={{
            fontFamily: '"Cal Sans", "Inter", sans-serif',
            fontWeight: 600,
            fontSize: 18,
            letterSpacing: "-0.02em",
            color: "#111827",
            cursor: "pointer",
            display: { xs: "block", md: "none" },
          }}
          onClick={() => navigate(localizePath("/"))}
        >
          RepRadar
        </Typography>
      </Box>

      {/* Right side: Language, Invite, User profile */}
      {user && (
        <Box display="flex" alignItems="center" gap={1.5}>
          <LanguageSelector />

          {canManageMeetings && (
            <Button
              size="small"
              startIcon={<PersonAddIcon />}
              onClick={handleInvite}
              disabled={inviteLoading}
              sx={{
                textTransform: "none",
                color: "#6b7280",
                borderColor: "#e5e7eb",
                fontWeight: 500,
                fontSize: 13,
                "&:hover": {
                  color: "#111827",
                  bgcolor: "#f9fafb",
                },
              }}
            >
              {inviteCopied ? t("common.copied") : inviteLoading ? "..." : t("header.invite")}
            </Button>
          )}

          <IconButton
            onClick={handleMenuOpen}
            size="small"
            sx={{
              p: 0,
              "&:hover": {
                "& .user-avatar": {
                  boxShadow: "0 0 0 2px rgba(17, 24, 39, 0.15)",
                },
              },
            }}
          >
            <Box
              className="user-avatar"
              sx={{
                width: 34,
                height: 34,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: user.photoURL ? undefined : avatarColor(user?.email),
                backgroundImage: user.photoURL ? `url(${user.photoURL})` : undefined,
                backgroundSize: "cover",
                backgroundPosition: "center",
                color: user.photoURL ? undefined : "#ffffff",
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: "-0.02em",
                transition: "box-shadow 0.15s ease-in-out",
              }}
            >
              {user.photoURL ? null : getInitials(user?.email)}
            </Box>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={!!anchorEl}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{
              paper: {
                sx: {
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                  border: "1px solid #e5e7eb",
                  borderRadius: 1,
                  minWidth: 200,
                  mt: 1,
                },
              },
            }}
          >
            <MenuItem disabled sx={{ fontSize: 12, color: "#6b7280" }}>
              {user.email}
            </MenuItem>
            {canManageMeetings && (
              <MenuItem
                onClick={handleInvite}
                disabled={inviteLoading}
                sx={{ fontSize: 13, py: 1 }}
              >
                <PersonAddIcon sx={{ mr: 1.5, fontSize: 18, color: "#6b7280" }} />
                {t("header.inviteTeamMember")}
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                handleMenuClose();
                navigate(localizePath("/settings"));
              }}
              sx={{ fontSize: 13, py: 1 }}
            >
              <SettingsIcon sx={{ mr: 1.5, fontSize: 18, color: "#6b7280" }} />
              {t("header.settings")}
            </MenuItem>
            <MenuItem onClick={handleSignOut} sx={{ fontSize: 13, py: 1 }}>
              <LogoutIcon sx={{ mr: 1.5, fontSize: 18, color: "#6b7280" }} />
              {t("header.signOut")}
            </MenuItem>
          </Menu>
        </Box>
      )}

      {/* Dialog for Creating Team */}
      <Dialog
        open={teamNameDialogOpen}
        onClose={() => !inviteLoading && setTeamNameDialogOpen(false)}
        slotProps={{
          paper: {
            sx: { borderRadius: 1 },
          },
        }}
      >
        <DialogTitle>{t("header.createTeam")}</DialogTitle>
        <DialogContent sx={{ minWidth: { xs: 280, sm: 400 } }}>
          <TextField
            autoFocus
            margin="dense"
            label={t("header.teamName")}
            fullWidth
            variant="outlined"
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCreateTeamWithName()}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setTeamNameDialogOpen(false)}
            disabled={inviteLoading}
            variant="outlined"
            size="small"
          >
            {t("header.cancel")}
          </Button>
          <Button
            onClick={handleCreateTeamWithName}
            disabled={!teamName.trim() || inviteLoading}
            variant="contained"
            size="small"
            sx={{ bgcolor: "#111827", "&:hover": { bgcolor: "#374151" } }}
          >
            {inviteLoading ? t("header.creating") : t("header.create")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbars */}
      <Snackbar
        open={inviteCopied}
        autoHideDuration={2000}
        onClose={() => setInviteCopied(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" sx={{ width: "100%", borderRadius: 1 }}>
          {t("common.copied")}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!inviteError}
        autoHideDuration={inviteError?.startsWith("http") ? 10000 : 5000}
        onClose={() => setInviteError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={inviteError?.startsWith("http") ? "info" : "error"}
          onClose={() => setInviteError(null)}
          sx={{ width: "100%", borderRadius: 1 }}
        >
          {inviteError?.startsWith("http") ? (
            <>{t("header.inviteLinkCopy")} {inviteError}</>
          ) : (
            inviteError
          )}
        </Alert>
      </Snackbar>
    </Box>
  );
}
